import { useCallback, useEffect, useRef, useState } from "react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./CarryallPaywall.module.scss"

/**
 * CARRYALL (`/carryall`) — a self-contained NON-ONBOARDING upsell for Private
 * Photo Vault, built on the one mechanic no prior paywall touches: a real brass
 * ZIPPER you drag shut.
 *
 * The concept: your memories are already here — loose in an open case, living on
 * this one phone. Premium is the moment you *zip it up*. Drag the pull across and
 * the case seals: loose photos straighten and settle, a lock charm snaps onto the
 * slider, and the four things Premium actually does stitch alight one by one —
 * Locked · Backed up · Every device · Full quality. It's the small, satisfying
 * ritual of putting something precious away safe.
 *
 * Behavioural spine: COMPLETION + ENDOWMENT (the photos are already yours; the
 * screen just asks you to finish sealing them) plus honest contrast framing
 * ("on this phone" → "backed up everywhere"). No fear, no fake urgency, no
 * invented stats. Never implies losing data — the reassurance line is explicit
 * that your photos are always yours.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/carryall.md
 *   - Layout:      a full-bleed premium leather CARRYALL — a horizontal case whose
 *                  brass zipper is the interactive spine, photos packed inside,
 *                  pricing docked below as luggage tags. Not a centered card, grid,
 *                  jar, book, receipt, scope, map or door.
 *   - Interaction: DRAG-TO-SEAL — a zipper pull you drag left→right (0→1), with a
 *                  tap toggle and a short auto-demo that teaches the gesture. A
 *                  zip mechanic, unlike any hold/scrub/dial/crank/flip/cut/pour.
 *   - Register:    tactile, cozy, reassuring — the "zzzip" of packing up something
 *                  you love. Not fear, luxury, nostalgia, awe or deadpan.
 *   - Art:         warm cognac LEATHER + BRASS hardware + cream stitching on soft
 *                  off-white; real bundled memory photos packed inside so it reads
 *                  as a photo vault in three seconds. Blue = the sealed/protected
 *                  accent; amber = the still-open state.
 *   - Value:       CONSOLIDATION / peace-of-mind — everything gathered into one
 *                  private place you can close, anchored to four real features.
 */

// Real bundled memory photos (public/recall/*.jpg — no runtime egress). The
// emotional + brand anchor: the things you're packing away safe.
const PACKED = [
  "sunset-1.jpg",
  "beach-2.jpg",
  "dog-1.jpg",
  "coffee-1.jpg",
  "flower-2.jpg",
  "mountain-3.jpg",
]

// Per-photo "loose" tilt (deg) that relaxes to 0 as the case seals.
const TILT = [-6, 4, -3, 5, -5, 3]

// The four things Premium actually does, stitched as tags on the case face.
// Each lights as the zip crosses its threshold.
const TAGS = [
  { key: "locked", label: "Locked", at: 0.28 },
  { key: "backed", label: "Backed up", at: 0.5 },
  { key: "devices", label: "Every device", at: 0.72 },
  { key: "quality", label: "Full quality", at: 0.98 },
]

const easeOutCubic = (k: number) => 1 - Math.pow(1 - k, 3)
const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

const CarryallPaywall = () => {
  const { state } = useAppState()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const y = yearlyProduct?.price ?? 35.99
  const m = monthlyProduct?.price ?? 13.99
  const savingsPct = m ? Math.round((1 - y / (m * 12)) * 100) : 0

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── The zipper: seal is 0 (wide open) → 1 (sealed). Mirrored to a ref so the
  //    rAF animator and pointer math always read the latest value. ──────────
  const [seal, _setSeal] = useState(0)
  const sealRef = useRef(0)
  const setSeal = useCallback((v: number) => {
    const c = clamp01(v)
    sealRef.current = c
    _setSeal(c)
  }, [])

  const rafRef = useRef<number | undefined>(undefined)
  const cancelAnim = () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    rafRef.current = undefined
  }

  const animateSealTo = useCallback(
    (target: number, dur = 520) => {
      cancelAnim()
      const from = sealRef.current
      const start = performance.now()
      const step = (t: number) => {
        const k = Math.min(1, (t - start) / dur)
        setSeal(from + (target - from) * easeOutCubic(k))
        if (k < 1) rafRef.current = requestAnimationFrame(step)
        else rafRef.current = undefined
      }
      rafRef.current = requestAnimationFrame(step)
    },
    [setSeal],
  )

  const [dragging, setDragging] = useState(false)
  const trackRef = useRef<HTMLDivElement>(null)

  const sealFromClientX = useCallback(
    (clientX: number) => {
      const el = trackRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const pad = r.width * 0.07 // pull half-width guard at each end
      const usable = r.width - pad * 2
      setSeal((clientX - r.left - pad) / usable)
    },
    [setSeal],
  )

  const onPointerDown = (e: React.PointerEvent) => {
    cancelAnim()
    setDragging(true)
    e.currentTarget.setPointerCapture?.(e.pointerId)
    sealFromClientX(e.clientX)
    native.logMixpanelEvent("html_carryall_grab", {})
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (dragging) sealFromClientX(e.clientX)
  }
  const endDrag = () => {
    if (!dragging) return
    setDragging(false)
    // Snap: committed past the shoulder → seal home; barely started → fall open.
    const v = sealRef.current
    if (v > 0.6) {
      animateSealTo(1, 360)
      native.logMixpanelEvent("html_carryall_sealed", { via: "drag" })
    } else if (v < 0.12) {
      animateSealTo(0, 300)
    }
  }

  const sealed = seal > 0.985

  const toggleSeal = () => {
    if (dragging) return
    if (sealed) animateSealTo(0.14, 520)
    else {
      animateSealTo(1, 460)
      native.logMixpanelEvent("html_carryall_sealed", { via: "tap" })
    }
  }

  // Auto-demo: a single unhurried "zzip" almost-closed, then relax back open —
  // teaching the gesture and leaving the case inviting completion.
  const didDemo = useRef(false)
  useEffect(() => {
    if (didDemo.current) return
    didDemo.current = true
    const t1 = setTimeout(() => animateSealTo(0.9, 900), 700)
    const t2 = setTimeout(() => animateSealTo(0.16, 1050), 1780)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      cancelAnim()
    }
  }, [animateSealTo])

  const choosePlan = (key: "yearly" | "monthly") => {
    if (key === plan) return
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }

  const handleClose = () => native.postNativeEvent("close")

  const purchase = () => {
    // Pressing the button finishes the ritual: the case seals, then we subscribe.
    animateSealTo(1, 380)
    native.logMixpanelEvent("html_carryall_purchase", { plan })
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const onTrackKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      toggleSeal()
    } else if (e.key === "ArrowRight") {
      e.preventDefault()
      setSeal(sealRef.current + 0.12)
    } else if (e.key === "ArrowLeft") {
      e.preventDefault()
      setSeal(sealRef.current - 0.12)
    }
  }

  return (
    <div
      className={`${styles.page} ${sealed ? styles.isSealed : ""}`}
      style={{ ["--seal" as any]: seal.toFixed(4) }}
    >
      <div className={styles.grain} aria-hidden />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden>
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.5 4.5 6v6c0 4.6 3.2 7.9 7.5 9.4 4.3-1.5 7.5-4.8 7.5-9.4V6L12 2.5Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <rect
                x="8.5"
                y="11"
                width="7"
                height="6"
                rx="1.4"
                fill="currentColor"
              />
              <path
                d="M9.6 11V9.3a2.4 2.4 0 0 1 4.8 0V11"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <h1 className={styles.headline}>
        Zip your memories
        <br />
        somewhere safe.
      </h1>
      <p className={styles.sub}>
        Everything you love is already here, living on this one phone. Premium
        is the moment you close it up.
      </p>

      {/* ── The carryall ─────────────────────────────────────────────── */}
      <div className={styles.case}>
        <span className={styles.status} aria-live="polite">
          <i className={styles.statusDot} aria-hidden />
          {sealed ? "Sealed · backed up everywhere" : "Open · on this phone"}
        </span>

        {/* the mouth: a live zipper you drag shut */}
        <div
          className={styles.mouth}
          ref={trackRef}
          role="slider"
          tabIndex={0}
          aria-label="Zip the case shut"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(seal * 100)}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onTrackKey}
        >
          <span className={styles.railTop} aria-hidden />
          <span className={styles.railBottom} aria-hidden />
          <span className={styles.channel} aria-hidden />
          <span
            className={styles.seam}
            aria-hidden
            style={{ width: `calc(var(--seal) * 100%)` }}
          />
          <span
            className={styles.pull}
            aria-hidden
            style={{ left: `calc(7% + var(--seal) * 86%)` }}
          >
            <span className={styles.pullTab}>
              <i />
              <i />
            </span>
            <span className={styles.charm}>
              <svg viewBox="0 0 24 24" width="14" height="14">
                <rect
                  x="5"
                  y="10.5"
                  width="14"
                  height="10.5"
                  rx="2"
                  fill="currentColor"
                />
                <path
                  d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </span>
          </span>
        </div>

        {/* what's packed inside — always visible, settles as you seal */}
        <div className={styles.packed}>
          {PACKED.map((file, i) => (
            <span
              className={styles.photo}
              key={file}
              style={{
                ["--tilt" as any]: `${TILT[i]}deg`,
                ["--d" as any]: i,
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/recall/${file}`}
                alt=""
                aria-hidden
              />
            </span>
          ))}
          <span className={styles.sheen} aria-hidden />
        </div>

        {/* feature tags stitched to the case face, lighting as you seal */}
        <ul className={styles.tags}>
          {TAGS.map((t) => (
            <li
              key={t.key}
              className={`${styles.tag} ${seal >= t.at ? styles.tagOn : ""}`}
            >
              <span className={styles.tagHole} aria-hidden />
              {t.label}
            </li>
          ))}
        </ul>

        <span className={styles.hint}>
          {sealed
            ? "Sealed — drag back to peek inside"
            : "Drag the zip across to seal it up"}
        </span>
      </div>

      {/* ── Pricing: yearly featured, monthly secondary ──────────────── */}
      <div className={styles.pricing}>
        <button
          className={`${styles.yearTag} ${plan === "yearly" ? styles.on : ""}`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.punch} aria-hidden />
          <span className={styles.yearMain}>
            <span className={styles.yearName}>
              Yearly
              <span className={styles.rec}>Best value</span>
            </span>
            <span className={styles.yearPrice}>
              {yearlyPrice}
              <em>/year</em>
            </span>
            <span className={styles.yearPer}>≈ {yearlyPerMonth} / month</span>
          </span>
          {savingsPct > 0 && (
            <span className={styles.save}>
              Save {savingsPct}%<em>vs monthly</em>
            </span>
          )}
        </button>

        <button
          className={`${styles.monthTag} ${plan === "monthly" ? styles.on : ""}`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthRadio} aria-hidden />
          <span className={styles.monthName}>Monthly</span>
          <span className={styles.monthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaMain}>
          {plan === "yearly" && hasTrial
            ? `Try ${trialDays} Days Free, Then Seal My Year`
            : plan === "yearly"
              ? "Seal My Year"
              : "Seal Month to Month"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? hasTrial
              ? `then ${yearlyPrice}/year · ≈ ${yearlyPerMonth}/mo`
              : `${yearlyPrice}/year · ≈ ${yearlyPerMonth}/mo`
            : `${monthlyPrice}/mo`}
        </span>
      </button>

      <p className={styles.reassure}>
        {plan === "yearly" && hasTrial ? "No charge today · " : ""}
        Cancel anytime · Auto-renews until canceled · Your photos are always
        yours
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default CarryallPaywall
